import React, { useMemo } from "react";

/**
 * Side-by-side skill heatmap.
 *
 * Rows: union of skill names across all candidates' competencies.
 * Columns: candidates.
 * Cell: candidate's score on that skill, colored by band.
 *
 * Plan-doc bands (0–100 scale):
 *   90+ emerald-500   75–89 emerald-300
 *   60–74 amber-400   40–59 orange-500   <40 red-500
 *
 * If candidate has no row for a skill → "—" muted cell.
 */
export default function HeatmapGrid({ candidates }) {
  const skillNames = useMemo(() => {
    const set = new Set();
    (candidates || []).forEach((c) => {
      (c.competencies || []).forEach((k) => {
        if (k?.skillName) set.add(k.skillName);
      });
    });
    return Array.from(set).sort();
  }, [candidates]);

  // Pre-index candidate scores per skill for fast lookup.
  const lookup = useMemo(() => {
    const m = new Map();
    (candidates || []).forEach((c) => {
      const inner = new Map();
      (c.competencies || []).forEach((k) => {
        if (k?.skillName != null) inner.set(k.skillName, k);
      });
      m.set(c.userId, inner);
    });
    return m;
  }, [candidates]);

  if (!candidates || candidates.length === 0 || skillNames.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6 text-center text-sm text-vf-muted">
        No competency data to compare.
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-5">
      <div className="px-4 py-3 border-b border-slate-200 flex items-baseline justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-vf-muted font-semibold">
            Skills heatmap
          </div>
          <div className="text-[11px] text-vf-muted mt-0.5">
            {skillNames.length} skill{skillNames.length === 1 ? "" : "s"} · {candidates.length} candidates
          </div>
        </div>
        <Legend />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              <th className="sticky left-0 bg-slate-50 z-10 text-left p-2 text-xs text-vf-muted uppercase font-semibold min-w-[160px]">
                Skill
              </th>
              {candidates.map((c) => (
                <th
                  key={c.userId}
                  className="p-2 text-xs text-vf-base font-semibold text-center bg-slate-50"
                  style={{ minWidth: 80 }}
                  title={c.fullName}
                >
                  <div className="truncate max-w-[120px] mx-auto">{c.fullName}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {skillNames.map((skill) => (
              <tr key={skill} className="border-t border-slate-100">
                <td className="sticky left-0 bg-white z-10 p-2 text-xs text-vf-base font-medium border-r border-slate-100">
                  {skill}
                </td>
                {candidates.map((c) => {
                  const k = lookup.get(c.userId)?.get(skill);
                  const score = k?.score == null ? null : Number(k.score);
                  return (
                    <td
                      key={c.userId}
                      className="p-0 text-center"
                      title={
                        k
                          ? `${c.fullName} · ${skill}: ${score == null ? "—" : score.toFixed(1)}${k.band ? ` (${k.band})` : ""}`
                          : `${c.fullName} · ${skill}: no data`
                      }
                    >
                      <Cell score={score} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const BAND_STYLE = (score) => {
  if (score == null) return { bg: "#F8FAFC", fg: "#94A3B8" };
  const s = Number(score);
  if (s >= 90) return { bg: "#10B981", fg: "#FFFFFF" }; // emerald-500
  if (s >= 75) return { bg: "#6EE7B7", fg: "#064E3B" }; // emerald-300
  if (s >= 60) return { bg: "#FBBF24", fg: "#78350F" }; // amber-400
  if (s >= 40) return { bg: "#F97316", fg: "#FFFFFF" }; // orange-500
  return { bg: "#EF4444", fg: "#FFFFFF" }; // red-500
};

function Cell({ score }) {
  const { bg, fg } = BAND_STYLE(score);
  return (
    <div
      className="text-sm font-semibold tabular-nums leading-none flex items-center justify-center"
      style={{
        background: bg,
        color: fg,
        height: 40,
        margin: 2,
        borderRadius: 4,
      }}
    >
      {score == null ? "—" : score.toFixed(0)}
    </div>
  );
}

function Legend() {
  const bands = [
    { label: "90+", style: BAND_STYLE(95) },
    { label: "75–89", style: BAND_STYLE(80) },
    { label: "60–74", style: BAND_STYLE(65) },
    { label: "40–59", style: BAND_STYLE(50) },
    { label: "<40", style: BAND_STYLE(20) },
  ];
  return (
    <div className="flex items-center gap-1.5 text-[10px]">
      {bands.map((b) => (
        <span
          key={b.label}
          className="px-1.5 py-0.5 rounded font-semibold"
          style={{ background: b.style.bg, color: b.style.fg }}
        >
          {b.label}
        </span>
      ))}
    </div>
  );
}
